import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { OverlayPanel } from 'primeng/overlaypanel';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { SearchService } from 'src/app/api/search.service';
import { DetailsPage as TaskDetailsPage } from 'src/app/tasks/details/details.page';
import * as dayjs from 'dayjs';
import { ApplyUseProjectDates, TaskDueDateLabel, UsesProjectDates } from '../../../services/task-project-dates.util';
import { GlobalService } from 'src/app/api/global.service';
import { TimeAndBillingService } from 'src/app/time-and-billing/time-and-billing.service';
import { Table } from 'primeng/table';
import { CreatePage as CreateTaskPage } from 'src/app/tasks/create/create.page';
import { EditPage as EditTaskPage } from 'src/app/tasks/edit/edit.page';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';
import { SelectComponent as SkillsSelectPage } from 'src/app/components/tags/select/select.component';
import { TaskStatus } from '../TaskStatus';
import { FilterService } from 'primeng/api';
import { DetailsPage as ProjectDetailsPage } from 'src/app/projects/details/details.page';
import { CookieService } from 'ngx-cookie-service';
import { QuickCommentComponent } from '../../comments/quick-add/quick-add.component';
import { DetailsPage as ClientDetailsPage } from 'src/app/clients/details/details.page';
import { DetailsPage as CompanyDetailsPage } from 'src/app/companies/details/details.page';
import { TablePdfExportService } from 'src/app/api/table-pdf-export.service';
import { TableExcelExportService } from 'src/app/api/table-excel-export.service';
import { StatusPipe } from 'src/app/pipes/tasks/status.pipe';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';

@Component({
  selector: 'app-task-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit {

  @ViewChildren(Table) tables: QueryList<Table>;
  @ViewChild('dateOverlay') dateOverlay: OverlayPanel;
  @Input() tasks_with_my_skills = false;
  @Input() active_tasks_only = false;
  @Input() overdue_tasks_only = false;
  @Input() in_progress_tasks_only = false;
  @Input() awaiting_client_tasks_only = false;
  @Input() start_date_passed = false;
  @Input() tasks_view: string = "all";
  // Filters by the current user's role on the task's PARENT PROJECT (task.project.team_members), not
  // the task itself.
  @Input() project_person_filter: 'all' | 'worker' | 'follower' = 'all';
  @Input() showRelativeDates: boolean = false;
  // When set, these are the only data columns shown and this is their order (e.g. the tasks of a project template being
  // picked). The Relative Due Date column (showRelativeDates) always comes after them.
  @Input() forced_columns: string[] = [];
  @Input() show_skeleton: boolean = false;
  @Input() skeleton_repeat: number = 1;
  @Input() tasks: any = null;
  @Input() show_paging: any = null;
  @Input() allow_filter: boolean = true;
  @Input() show_expander = true;
  @Input() edit_mode = false;
  @Input() project: any = null;
  @Input() load_from_state = false;
  @Input() create_mode = false;
  @Input() url_mod = '';
  @Input() ignore_cookies = false;
  @Input() can_edit = true;
  // Which date column the table is sorted by, earliest first (sortOrder stays ascending below) -
  // e.g. the home page switches this between 'due_date'/'start_date' to match whichever date type
  // its own Due/Starting filter is currently scoped to.
  @Input() sort_field: string = 'start_date';
  @Input() show_checkbox = false;
  @Input() selectedTasks: any[] = [];
  // How many tasks can be ticked at once (a picker that wants one task sets 1, which also hides Select All)
  @Input() max_allowed_selections = 9999999999;
  @Output() selectedTasksEvent = new EventEmitter<any>();
  // Shows the multi-select toggle button (next to Search) that lets the user opt into the
  // checkbox column / "Mark Complete" / "Change Internal Status" toolbar. Kept separate from
  // show_checkbox in case some future caller wants the checkbox column always on without the
  // toggle button (mirrors the projects table's allow_bulk_actions).
  @Input() allow_bulk_actions = false;
  // On/off state of the multi-select toggle button. Off by default - the checkbox column and bulk
  // toolbar only appear once the user explicitly turns this on.
  bulk_select_active = false;
  bulk_internal_status_guid: string = '';
  bulk_assigned_member_guid: string = '';

  // Whether the checkbox column should render - either because a caller always wants it
  // (show_checkbox) or because the user turned on multi-select mode.
  get show_selection_column(): boolean {
    return this.show_checkbox || (this.allow_bulk_actions && this.bulk_select_active);
  }

  ToggleBulkSelectMode() {
    this.bulk_select_active = !this.bulk_select_active;
    if (!this.bulk_select_active) this.ClearSelectedTasks();
  }

  team_member: any = [];
  /**
   * Array of column keys to hide. Example: ['tags','groups'] to hide tags and groups columns
   */
  @Input() hideColumns: string[] = [];
  @Input() hideStepColumns: string[] = [];
  task_templates: any;
  selected_task_template: any;

  max_per_page = 100;
  @Output() taskModified = new EventEmitter<{ data: any }>();
  @Output() refreshTasks = new EventEmitter<{ data: any }>();
  // Fired with a project that was just reloaded, so the list that owns the tasks can add the tasks it gained and drop the ones it lost
  @Output() projectRefreshed = new EventEmitter<{ project: any }>();
  today: Date;

  // Search functionality
  is_searching = false;
  all_tasks: any[] = []; // Store original unfiltered tasks
  current_search_query: any = null;

  /**
   * Column configuration for the column picker
   */
  availableColumns: ColumnConfig[] = [
    { key: 'title', label: 'Title', visible: true },
    { key: 'internal_status', label: 'Internal Status', visible: false },
    { key: 'status', label: 'System Status', visible: true },
    { key: 'linked_name', label: 'Linked To', visible: true },
    { key: 'client_year_end_date', label: 'Client Year End Date', visible: true },
    { key: 'project_title', label: 'Project Title', visible: true },
    { key: 'team_member_assigned_name', label: 'Team Member Assigned', visible: true },
    { key: 'progress', label: 'Progress', visible: true },
    { key: 'estimated_time', label: 'Estimated Time', visible: true },
    { key: 'time_recorded', label: 'Time Recorded', visible: true },
    { key: 'estimated_time_remaining', label: 'Estimated Time Remaining', visible: true },
    { key: 'start_date', label: 'Start Date', visible: true },
    { key: 'due_date', label: 'Due Date', visible: true },
    { key: 'completed_date', label: 'Completed Date', visible: true },
    { key: 'skills', label: 'Skills', visible: true },
    { key: 'document_request_complete', label: 'Client Document Request Complete', visible: true },
    { key: 'last_client_document_upload', label: 'Last Client Document Upload', visible: true },
  ];

  taskInternalStatuses: any[] = [];
  allSkills: any[] = [];
  activeTaskFilterLabels: string[] = [];
  matchModeOptions = [
    { label: 'Includes Any', value: 'includesAny' },
    { label: 'Includes All', value: 'includesAll' },
  ];
  singleMatchModeOptions = [
    { label: 'Includes Any', value: 'singleIncludesAny' },
    { label: 'Includes All', value: 'singleIncludesAll' },
  ];

  // Not private - tasks.page.html binds this (via the taskTable ViewChild) into app-filter-presets'
  // [fieldLabelMap] so saved-filter tooltips can describe filters in the same plain-English labels
  // the "Column filters active" indicator already uses.
  readonly taskFilterLabelMap: { [field: string]: string } = {
    title: 'Title',
    internal_status: 'Internal Status',
    status: 'System Status',
    linked_name: 'Linked To',
    client_year_end_date: 'Client Year End Date',
    project_title: 'Project',
    assigned_member_guid: 'Assigned To',
    skills: 'Skills',
    progress: 'Progress',
    completed_date: 'Completed Date',
    last_client_document_upload: 'Last Client Upload',
    document_request_complete: 'Document Request Complete',
  };

  onTaskTableFilter(event: any) {
    this.activeTaskFilterLabels = this.computeActiveFilterLabels(event.filters, this.taskFilterLabelMap);
  }

  clearTaskFilters() {
    if (this.tables?.first) this.clearTableFilters(this.tables.first);
    this.activeTaskFilterLabels = [];
  }

  // Saved filter presets (see FilterPresetsComponent, wired in via tasks.page.html's taskTable
  // ViewChild same as the quick search) - mirrors components/projects/table's
  // GetCurrentTableFilters/ApplyTableFilters, but only the first table instance since clearTaskFilters
  // itself only ever targets tables.first too (tasks doesn't split into one <p-table> per group).
  GetCurrentTableFilters = (): any => {
    return (this.tables?.first as any)?.filters ?? null;
  };

  ApplyTableFilters(filters: any) {
    const applied = filters ? JSON.parse(JSON.stringify(filters)) : {};
    if (this.tables?.first) {
      (this.tables.first as any).filters = applied;
      (this.tables.first as any)._filter();
    }
    this.activeTaskFilterLabels = this.computeActiveFilterLabels(applied, this.taskFilterLabelMap);
  }

  private clearTableFilters(t: Table): void {
    if (!t) return;
    (t as any).tableService.onResetChange();
    if ((t as any).isStateful()) (t as any).clearState();
    (t as any)._filter();
  }

  private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
    if (!filters) return [];
    const active: string[] = [];
    for (const field of Object.keys(labelMap)) {
      const raw = filters[field];
      if (!raw) continue;
      const constraints = Array.isArray(raw) ? raw : [raw];
      const hasValue = constraints.some((c: any) => {
        if (c.value === null || c.value === undefined) return false;
        if (Array.isArray(c.value)) return c.value.length > 0;
        if (typeof c.value === 'string') return c.value.trim().length > 0;
        return true;
      });
      if (hasValue) active.push(labelMap[field]);
    }
    return active;
  }
  taskStatusOptions = [
    { label: 'Ready to Start', value: TaskStatus.inactive },
    { label: 'Started - In Progress', value: TaskStatus.active },
    { label: 'Awaiting Client', value: TaskStatus.awaiting_client },
    { label: 'Completed', value: TaskStatus.completed },
    { label: 'Client Intervention', value: TaskStatus.client_hold },
    { label: 'Paused', value: TaskStatus.paused },
    { label: 'Awaiting Previous Task', value: TaskStatus.awaiting_previous_task },
  ];

  //CSS classes
  @Input() no_margin = false;
  @Input() loaded_tasks = true;
  @Output() tasksChange = new EventEmitter<any[]>();

  team_members: any[] = [];
  // Options for the Team Member Assigned column filter's p-multiSelect - a plain field recomputed
  // once whenever team_members is (re)loaded (see FormatData), NOT a getter. A getter that builds a
  // new array on every change-detection cycle is what made the Health filter's multiSelect appear
  // unclickable (identity changes on every tick reset the overlay); this avoids that same trap.
  teamMemberFilterOptions: any[] = [];
  private _initialized = false;

  private RefreshTeamMemberFilterOptions() {
    this.teamMemberFilterOptions = [{ guid: '', display_name: 'Unassigned' }, ...this.team_members];
  }

  // Inline date editing state
  inline_edit_task: any = null;
  inline_edit_task_guid: string = null;
  inline_edit_field: string = null;
  inline_edit_date: Date = null;
  inline_edit_min_date: Date = null;
  inline_edit_max_date: Date = null;
  inline_edit_disabled_tooltip: string = null;

  // Inline team member editing state
  inline_edit_member_task_guid: string = null;

  constructor(private modalController: ModalController, private apiService: ApiService, public globalService: GlobalService, private alertController: AlertController, private searchService: SearchService, private cookieService: CookieService, private filterService: FilterService, private tablePdfExportService: TablePdfExportService, private tableExcelExportService: TableExcelExportService, private timeAndBillingService: TimeAndBillingService) {


  }


  ngOnInit() {

    //this.FullRefresh();
  }

  // Forces the columns (and their order) from forced_columns: everything else is hidden and the picker's saved order is ignored
  private ApplyForcedColumns() {
    if (!this.forced_columns?.length) return;
    const forced = this.availableColumns.filter(col => this.forced_columns.includes(col.key));
    const others = this.availableColumns.filter(col => !this.forced_columns.includes(col.key));
    forced.sort((a, b) => this.forced_columns.indexOf(a.key) - this.forced_columns.indexOf(b.key));
    this.availableColumns = [...forced, ...others];
    this.hideColumns = [...this.hideColumns.filter(key => !this.forced_columns.includes(key)), ...others.map(col => col.key).filter(key => !this.hideColumns.includes(key))];
  }

  async FullRefresh() {
    this.syncColumnsFromHideColumns();
    this.RestoreFilterCookies();
    // After the saved column choices, so they can't change what is forced
    this.ApplyForcedColumns();
    this.syncColumnsFromHideColumns();
    await this.GetTeamMember();
    this.GetTaskInternalStatuses();
    this.GetAllSkills();

    this.filterService.register('taskInternalStatusMatch', (guid: string, selected: string[]) => {
      if (!selected || selected.length === 0) return true;
      return selected.includes(guid);
    });
    this.filterService.register('taskStatusMatch', (value: number, selected: number[]) => {
      if (!selected || selected.length === 0) return true;
      return selected.includes(value);
    });
    this.filterService.register('taskSkillsMatch', (taskSkills: any[], selectedGuids: string[]) => {
      if (!selectedGuids || selectedGuids.length === 0) return true;
      if (!taskSkills || taskSkills.length === 0) return false;
      return selectedGuids.every(guid => taskSkills.some(s => s.guid === guid));
    });

    // A task only ever has one assigned team member, so this is "Includes Any" only - same reasoning
    // as taskInternalStatusMatch/taskStatusMatch. '' represents Unassigned (see
    // RefreshTeamMemberFilterOptions), matching however assigned_member_guid comes back unassigned
    // (empty string, null, or undefined) rather than requiring an exact '' on the task itself.
    this.filterService.register('taskTeamMemberMatch', (guid: string, selected: string[]) => {
      if (!selected || selected.length === 0) return true;
      return selected.includes(guid || '');
    });

    // Only default to 25 the first time (no saved preference yet) - don't clobber a restored one
    if (this.show_paging && !this.restored_max_per_page) this.max_per_page = 25;
    this.today = dayjs().startOf('day').toDate();
    if (this.edit_mode) this.GetEditModeInformation();

    // Initialize tasks data for search functionality
    this.initializeTasksData();
    await this.FormatData();
    this.tasks = this.GetFilteredTasks(this.all_tasks);

  }

  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    if (data.success) {
      this.team_members = data.data;
    }
    if (!this.team_members) this.team_members = [];
  }

  async GetTaskInternalStatuses() {
    const data: any = await this.apiService.get('tags/tasks_internal_status');
    if (data.success) this.taskInternalStatuses = data.data || [];
    if (!this.taskInternalStatuses) this.taskInternalStatuses = []
  }

  async GetAllSkills() {
    const data: any = await this.apiService.get('tags/team_members');
    if (data?.success) this.allSkills = data.data || [];
  }

  GetInternalStatusByGuid(guid: string) {
    if (!guid || !this.taskInternalStatuses || this.taskInternalStatuses.length === 0) return null;
    return this.taskInternalStatuses.find(s => s.guid === guid) || null;
  }

  async TaskInternalStatusChange(task: any) {
    this.apiService.put(`tasks/${task.guid}`, { internal_status: task.internal_status });
  }

  async GetTeamMember() {
    let get_guid = '';
    // If url_mod includes a team_member param, extract guid and set this.team_member so the following API call can use it
    if (this.url_mod && typeof this.url_mod === 'string' && this.url_mod.includes('team_member=')) {
      const match = this.url_mod.match(/team_member=([^&'"]+)/);
      if (match && match[1]) {
        const raw = match[1].replace(/['"+]/g, '');
        const guid = decodeURIComponent(raw);
        get_guid = guid;
      }
    }
    else {
      let current_usr = await this.globalService.GetCurrentUser();
      get_guid = current_usr.guid;
    }
    let data: any = await this.apiService.get(`team_members/${get_guid}`);
    if (data.success) {

      this.team_member = data.data[0];
      //make the object cleaner
      if (!this.team_member.skills) this.team_member.skills = [];
      if (!this.team_member.projects) this.team_member.projects = [];
      if (!this.team_member.tasks) this.team_member.tasks = [];
    }

  }


  ngOnChanges(changes: SimpleChanges) {
    if (!this._initialized) {
      this._initialized = true;
      this.FullRefresh();
    } else {
      if (changes.url_mod) {
        this.GetTeamMember();
      }
      if (changes.tasks && changes.tasks.currentValue) {
        this.updateTasksData(changes.tasks.currentValue);
      }
    }

    if (changes.tasks_with_my_skills) {
      const previousValue = changes.tasks_with_my_skills.previousValue;
      const currentValue = changes.tasks_with_my_skills.currentValue;
      if (previousValue !== currentValue) {
        this.onTasksWithMySkillsChange(currentValue);
      }
    }
    if (changes.tasks_view) {
      const previousValue = changes.tasks_view.previousValue;
      const currentValue = changes.tasks_view.currentValue;
      if (previousValue !== currentValue) {
        this.onTaskViewChange(currentValue);
      }
    }
    if (changes.active_tasks_only) {
      const previousValue = changes.active_tasks_only.previousValue;
      const currentValue = changes.active_tasks_only.currentValue;
      if (previousValue !== currentValue) {
        this.onTaskViewChange(currentValue);
      }
    }
    if (changes.overdue_tasks_only) {
      const previousValue = changes.overdue_tasks_only.previousValue;
      const currentValue = changes.overdue_tasks_only.currentValue;
      if (previousValue !== currentValue) {
        this.onTaskViewChange(currentValue);
      }
    }
    if (changes.in_progress_tasks_only) {
      const previousValue = changes.in_progress_tasks_only.previousValue;
      const currentValue = changes.in_progress_tasks_only.currentValue;
      if (previousValue !== currentValue) {
        this.onTaskViewChange(currentValue);
      }
    }
    if (changes.awaiting_client_tasks_only) {
      const previousValue = changes.awaiting_client_tasks_only.previousValue;
      const currentValue = changes.awaiting_client_tasks_only.currentValue;
      if (previousValue !== currentValue) {
        this.onTaskViewChange(currentValue);
      }
    }
    if (changes.start_date_passed) {
      const previousValue = changes.start_date_passed.previousValue;
      const currentValue = changes.start_date_passed.currentValue;
      if (previousValue !== currentValue) {
        this.onTaskViewChange(currentValue);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.load_from_state) this.DeleteExpandedRows();
  }

  async DeleteExpandedRows() {
    setTimeout(() => {
      const stateKey = 'app-tasks-table';
      const savedState = localStorage.getItem(stateKey);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.expandedRowKeys) {
          delete parsed.expandedRowKeys;
          localStorage.setItem(stateKey, JSON.stringify(parsed));
        }
      }
    });
  }

  async GetEditModeInformation() {
    await this.GetTaskTemplates();
    if (!this.project.tasks) this.project.tasks = [];
    this.tasks = this.project.tasks;
    this.tasks.forEach(task => {
      this.GetTaskDetails(task, false);
    });
  }


  //When a step is completed, it fires the task modified event which passes the task it modified, and the step it modified so we can update the view
  async stepModified(eventData: { data: any }) {
    //We are going to find the task, and update it in the task array
    let returned_task_data = eventData.data.task;
    let index = this.tasks.findIndex(x => x.guid === returned_task_data.guid);
    await this.GetTaskDetails(this.tasks[index], false);
    this.taskModified.emit({ data: this.tasks[index] });
  }

  async FormatData() {
    if (!this.tasks) return;
    if (this.team_members.length == 0) await this.GetTeamMembers();
    this.RefreshTeamMemberFilterOptions();
    if(!this.tasks) return;

    // Build the "task.project" fallback snapshot once, with any already-hydrated `.project` stripped
    // off its sibling tasks first. Without this, cloning `this.project` per task (a) picks up whatever
    // nested `.project` earlier tasks in this same loop already got assigned (since the synchronous
    // prefix of each map callback runs in order before any of them await), and (b) compounds further
    // across every future save/load of the project - both turn into a quadratic-or-worse blowup that
    // can reach megabytes of JSON and hang the page.
    let projectSnapshot: any = null;
    if (this.project) {
      projectSnapshot = {
        ...this.project,
        tasks: (this.project.tasks || []).map((t: any) => {
          const { project, ...rest } = t;
          return rest;
        })
      };
    }

    const timeEntries: any[] = await this.timeAndBillingService.GetEntries();
    await Promise.all(this.tasks.map(async task => {
      if (projectSnapshot && !task.project) {
        task.project = JSON.parse(JSON.stringify(projectSnapshot));
      }
      task.project_title = task.project.title;
      task.time_recorded = this.CalculateTaskTimeRecorded(task.guid, timeEntries);
      task.estimated_time_remaining = (Number(task.estimated_time) || 0) - (Number(task.time_recorded) || 0);
      task.project_guid = task.project.guid;
      task.team_member_assigned_name = this.GetAssignedTeamMember(task.assigned_member_guid);

      if (this.edit_mode) {
        if (this.project) {
          task.company_guid = this.project.company_guid;
          task.client_guid = this.project.client_guid;
        }

      }
      if (task.is_fake) {
        if (task.project.client_guid) {
          task.client = await this.globalService.GetClient(task.project.client_guid);
          task.project.client = task.client;
          task.linked_name = task.client.display_name;
        }
        else if (task.project.company_guid) {
          task.company = await this.globalService.GetCompany(task.project.company_guid);
          task.project.company = task.company;
          task.linked_name = task.company.name;
        }
      }
      else {

        if (task.client_guid) {
          task.client = await this.globalService.GetClient(task.client_guid);
          task.linked_name = task.client.display_name;
        }
        else if (task.company_guid) {
          task.company = await this.globalService.GetCompany(task.company_guid);

          task.linked_name = task.company.name;
        }

      }
      // Only companies have a year end - blank for a contact
      task.client_year_end_date = !task.client && task.company?.year_end_date ? new Date(task.company.year_end_date) : null;
      task.start_date_time = new Date(task.start_date).getTime();
      if (task.completed_date && !(task.completed_date instanceof Date)) task.completed_date = new Date(task.completed_date);
      if (task.last_client_document_upload && !(task.last_client_document_upload instanceof Date)) task.last_client_document_upload = new Date(this.globalService.DateToUTCFormat(task.last_client_document_upload)); // stored in UTC without a Z
      if (task.document_request_complete && !(task.document_request_complete instanceof Date)) task.document_request_complete = new Date(this.globalService.DateToUTCFormat(task.document_request_complete)); // stored in UTC without a Z
      if (task.status != TaskStatus.completed && !this.IsPreviousTaskCompleted(task.project, task)) task.status = TaskStatus.awaiting_previous_task;
      task.latest_comment_date = [
        this.globalService.DateToUTCFormat(task.task_last_comment_date),
        this.globalService.DateToUTCFormat(task.step_last_comment_date)
      ].filter(d => !!d).reduce((max, d) => (!max || d > max ? d : max), null);
    }));
  }

  GetAssignedTeamMember(guid) {
    if (!guid) return 'Unassigned';
    if (guid == '') return 'Unassigned';
    if (this.team_members.length == 0) {
      return "";
    }

    const member = this.team_members.find(m => m.guid === guid);
    return member ? member.display_name : 'Unassigned';
  }

  private CalculateTaskTimeRecorded(task_guid: string, timeEntries: any[]): number | null {
    if (!task_guid || !timeEntries?.length) return null;
    let totalMinutes = 0;
    let found = false;
    for (const entry of timeEntries) {
      if (entry.task_guid !== task_guid) continue;
      if (!entry.start_date || !entry.end_date) continue;
      found = true;
      const diff = this.globalService.GetTimeDifferenceAsObject(entry.start_date, entry.end_date);
      totalMinutes += (diff.hours * 60) + diff.minutes;
    }
    return found ? totalMinutes : null;
  }

  FormatMinutesToDuration(minutes: number): string {
    const total = Number(minutes) || 0;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  }


  async GetTaskDetails(task, user_open) {
    if (this.create_mode) {
      task.loaded_task_details = true;
      return;
    }
    if (user_open) task.loaded_task_details = false;
    if (task.is_fake) {
      let fake_guid = task.guid;
      //Create the project
      this.globalService.presentLoading("Loading Task...", 99999999);
      let create_data: any = await this.apiService.post('projects', task.project);
      this.globalService.dismissLoading();

      if (create_data.success) {
        let tasks = create_data.data[0].tasks;
        let selected_task = null;
        tasks.forEach(task_loop => {
          if (task_loop.title == task.title) {
            selected_task = task_loop;
          }
        });
        selected_task.is_fake = false;
        Object.assign(task, JSON.parse(JSON.stringify(selected_task)));
        this.updateExpandedRowKeys(fake_guid, task.guid);

      }
      else {
        if (create_data.status_code == 208) {
          let project_guid = create_data.data;
          let project_data: any = await this.apiService.get(`projects/${project_guid}`);

          let tasks = project_data.data[0].tasks;
          let selected_task = null;
          tasks.forEach(task_loop => {
            if (task_loop.title == task.title) {
              selected_task = task_loop;
            }
          });
          selected_task.is_fake = false;
          Object.assign(task, JSON.parse(JSON.stringify(selected_task)));

          this.updateExpandedRowKeys(fake_guid, task.guid);
        }
      }
    }

    let task_details: any = await this.apiService.get(`tasks/${task.guid}`);
    task.steps = task_details.data[0].steps;
    task.progress = task_details.data[0].progress;
    task.status = task_details.data[0].status;
    if (task.status != TaskStatus.completed && !this.IsPreviousTaskCompleted(task.project, task)) task.status = TaskStatus.awaiting_previous_task;
    task.project_guid = task_details.data[0].project_guid;
    task.completed_date = this.globalService.ConvertDateFromBackend(task_details.data[0].completed_date);
    task.due_date = this.globalService.ConvertDateFromBackend(task_details.data[0].due_date);

    if (user_open) task.loaded_task_details = true;





  }


  async presentTaskDetails(task) {

    if (task.is_fake) {
      let fake_guid = task.guid;
      //Create the project
      this.globalService.presentLoading("Loading Task...", 99999999);
      let create_data: any = await this.apiService.post('projects', task.project);
      this.globalService.dismissLoading();

      if (create_data.success) {
        let tasks = create_data.data[0].tasks;
        let selected_task = null;
        tasks.forEach(task_loop => {
          if (task_loop.title == task.title) {
            selected_task = task_loop;
          }
        });
        const indexToRemove = this.tasks.findIndex((item) => item.guid === task.guid);
        this.tasks.splice(indexToRemove, 1);
        task = JSON.parse(JSON.stringify(selected_task));
        this.tasks.splice(indexToRemove, 0, task);
      }
      else {
        if (create_data.status_code == 208) {
          let project_guid = create_data.data;
          let project_data: any = await this.apiService.get(`projects/${project_guid}`);

          let tasks = project_data.data[0].tasks;
          let selected_task = null;
          tasks.forEach(task_loop => {
            if (task_loop.title == task.title) {
              selected_task = task_loop;
            }
          });
          const indexToRemove = this.tasks.findIndex((item) => item.guid === task.guid);
          this.tasks.splice(indexToRemove, 1);
          task = JSON.parse(JSON.stringify(selected_task));
          this.tasks.splice(indexToRemove, 0, task);
        }
      }
    }

    const modal = await this.modalController.create({
      component: TaskDetailsPage,
      cssClass: 'large-modal',
      componentProps: {
        guid: task.guid
      },
      backdropDismiss: false
    })
    modal.onDidDismiss()
      .then((data: any) => {
        if (data.data?.refresh) this.RefreshOneTask(task.guid);
      });
    return await modal.present()
  }

  // Refreshes the table without reloading the whole list: the given tasks are reloaded on their own (their actions too), and the
  // rest of each of their projects' tasks is reloaded from the project itself. Finishing a task or its actions can start the next
  // task (or finish others) in the project, and the project's task list is where that shows up.
  // Tasks that aren't in the table (filtered out, or new) are ignored - only rows already shown are updated.
  async RefreshTasksInPlace(task_guids: string[], extra_project_guids: string[] = []) {
    try {
      const rows = (task_guids || []).map(g => (this.tasks || []).find(t => t.guid === g)).filter(t => !!t);
      const project_guids = Array.from(new Set([
        ...rows.map(t => t.project_guid || t.project?.guid),
        ...(extra_project_guids || [])
      ].filter(g => !!g)));
      if (rows.length === 0 && project_guids.length === 0) return;

      const [task_results, project_results]: any[][] = await Promise.all([
        Promise.all(rows.map(row => this.apiService.get(`tasks/${row.guid}`))),
        Promise.all(project_guids.map(g => this.apiService.get(`projects/${g}`)))
      ]);

      const changed: any[] = [];
      const refreshed_projects: any[] = [];
      rows.forEach((row, i) => {
        const fresh = task_results[i]?.success ? task_results[i].data?.[0] : null;
        if (!fresh) throw new Error('task not found');
        // Overlaid on the row in place - a row keeps anything only the list works out, which FormatData fills in again
        Object.assign(row, fresh);
        changed.push(row);
      });

      project_guids.forEach((project_guid, i) => {
        const project_tasks: any[] = project_results[i]?.success ? (project_results[i].data?.[0]?.tasks || []) : [];
        if (project_results[i]?.success && project_results[i].data?.[0]) refreshed_projects.push(project_results[i].data[0]);
        project_tasks.forEach(project_task => {
          if (rows.some(r => r.guid === project_task.guid)) return;
          const row = this.tasks.find(t => t.guid === project_task.guid);
          if (row) {
            Object.assign(row, project_task);
            changed.push(row);
          }
        });
        // The rows work out "waiting on the previous task" from the tasks held on their project, so those are refreshed too
        if (project_tasks.length) {
          this.tasks.forEach(t => { if (t.project && (t.project_guid || t.project.guid) === project_guid) t.project.tasks = project_tasks; });
        }
      });

      await this.FormatData();
      changed.forEach(task => this.taskModified.emit({ data: task }));
      refreshed_projects.forEach(project => this.projectRefreshed.emit({ project }));
    } catch (e) {
      // Couldn't update just those rows - fall back to reloading the list
      this.refreshTasks.emit({ data: true });
    }
  }

  RefreshOneTask(guid: string) {
    return this.RefreshTasksInPlace([guid]);
  }


  async OpenAddComment(item: any) {
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal',
      componentProps: { url: 'tasks/comments', guid: item.guid, title: 'Add Comment to Task' }
    });
    return await modal.present();
  }

  async OpenQuickComment(item: any) {
    const modal = await this.modalController.create({
      component: TaskDetailsPage,
      cssClass: 'large-modal',
      componentProps: { guid: item.guid, activeIndex: 'Comments' },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.refresh) this.RefreshOneTask(item.guid);
    });
    return await modal.present();
  }

  GetCommentTooltip(item: any): string {
    if (!item.latest_comment_date) return 'No comments yet — click to add';
    return `Last comment: ${dayjs(item.latest_comment_date).format('MMM D, YYYY h:mm A')}`;
  }

  // A fake/preview task (used in create flows) only has the linked client/company on its
  // project, not on itself - see FormatData()'s is_fake branch.
  async OpenLinkedEntity(task: any, event?: Event) {
    if (event) event.stopPropagation();
    const client_guid = task.client_guid || task.project?.client_guid;
    const company_guid = task.company_guid || task.project?.company_guid;
    let modal: HTMLIonModalElement;
    if (client_guid) {
      modal = await this.modalController.create({
        component: ClientDetailsPage,
        cssClass: 'large-modal',
        componentProps: { client: { guid: client_guid } },
        backdropDismiss: false
      });
    } else if (company_guid) {
      modal = await this.modalController.create({
        component: CompanyDetailsPage,
        cssClass: 'large-modal',
        componentProps: { company: { guid: company_guid } },
        backdropDismiss: false
      });
    } else {
      return;
    }
    return await modal.present();
  }

  //Edit stuff
  async LoadFromTaskTemplates() {
    if (this.selected_task_template) {

      let task_data: any = await this.apiService.get(`templates/tasks/${this.selected_task_template.guid}`);
      let task_json: any = JSON.parse(task_data.data[0].json_str);
      task_json.template_guid = this.selected_task_template.guid;
      task_json.guid = this.globalService.createGUID();
      task_json.is_new = true;
      task_json.order_num = this.project.tasks.length + 1;
      task_json.front_end_guid = this.globalService.createGUID();
      this.project.tasks.push(task_json);
      //Fix the dates for the task
      // if(task_json.due_date_relative) {
      //   task_json.due_date
      // }

      this.ChangeProjectDates();
      await this.FormatData();
      this.selected_task_template = null;
    }
  }

  async GetTaskTemplates() {
    let data: any = await this.apiService.get('templates/tasks');
    if (data.success) {
      this.task_templates = data.data;
    }
  }

  ProjectDateConfilct(date_in) {
    if (this.project == null) return false;
    if ((date_in > this.project.internal_end_date) || date_in < this.project.internal_start_date) return true;

  }

  async EditTask(task) {
    const modal = await this.modalController.create({
      component: EditTaskPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        project: this.project,
        task: task,
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.GetTaskTemplates();
        this.ChangeProjectDates();
      }
    });
    return await modal.present()
  }

  async RemoveStep(task, step) {
    //Find the step in the task and remove it.
    const index = task.steps.findIndex(obj =>
      JSON.stringify(obj) === JSON.stringify(step)
    );
    if (index !== -1)
      task.steps.splice(index, 1);
  }


  async ChangeProjectDates() {
    this.tasks.forEach(task => {
      if (task.due_date_relative) {
        if (task.relative_to_start) {
          task.due_date = dayjs(this.project.internal_start_date).add(task.due_date_relative, 'day').format('YYYY-MM-DD');
        }
        else {
          task.due_date = dayjs(this.project.internal_end_date).add(task.due_date_relative * -1, 'day').format('YYYY-MM-DD');
        }
        ApplyUseProjectDates(task, this.project.internal_start_date, this.project.internal_end_date);
        if (task.due_date > this.project.internal_end_date) this.globalService.presentToastMessage(`Task: ${task.title} is due after the project is due.`, false);
        if (task.due_date < this.project.internal_start_date) this.globalService.presentToastMessage(`Task: ${task.title} is due before the project is started.`, false);
        // Action due dates are no longer used - just keep the stored value inside its task's dates.
        task.steps.forEach(step => {
          if (step.relative_date) {
            step.due_date = dayjs(task.start_date).add(step.due_date_relative, 'day').format('YYYY-MM-DD');
            if (step.due_date > task.due_date) step.due_date = task.due_date;
            if (step.due_date < task.start_date) step.due_date = task.start_date;
          }
        });
      }
      else {
        if (task.due_date > this.project.internal_end_date) this.globalService.presentToastMessage(`Task: ${task.title} is due after the project is due.`, false);
        if (task.due_date < this.project.internal_start_date) this.globalService.presentToastMessage(`Task: ${task.title} is due before the project is started.`, false);
      }

    });
  }

  async OpenCreateTaskModal() {
    const modal = await this.modalController.create({
      component: CreateTaskPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        project: this.project,
        edit_mode: false
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.GetTaskTemplates();
        data.data.order_num = this.project.tasks.length + 1;
        data.data.front_end_guid = this.globalService.createGUID();
        this.project.tasks.push(JSON.parse(JSON.stringify(data.data)));
        this.ChangeProjectDates();
      }
    });
    return await modal.present()
  }

  async DeleteTask(task) {
    var message = `Are you sure you would like to delete ${task.title}?`;

    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Change active status?',
      message: message,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {

          },
        },
        {
          text: 'OK',
          role: 'confirm',
          handler: async () => {
            let task_index = this.globalService.GetArrayIndex(this.tasks, task.guid);
            this.tasks.splice(task_index, 1);
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Initializes tasks data for search functionality
   */
  initializeTasksData() {
    if (this.tasks && !this.all_tasks.length) {
      this.all_tasks = [...this.tasks];
    }
  }

  /**
   * Performs search on tasks using the search service
   */
  async searchTasks(query: any) {
    this.is_searching = true;
    this.current_search_query = query;


    // Ensure we have original data
    this.initializeTasksData();

    // Use the search service to filter tasks
    const fieldMappingConfig = this.searchService.createTaskFieldMapping();
    const filteredTasks = this.searchService.filterDataByQuery(this.all_tasks, query, fieldMappingConfig);

    this.tasks = this.GetFilteredTasks(filteredTasks);
  }

  /**
   * Clears the current search and shows all tasks
   */
  clearTasksSearch() {
    this.is_searching = false;
    this.current_search_query = null;
    if (this.all_tasks.length > 0) {
      this.tasks = this.GetFilteredTasks([...this.all_tasks]);
    }
  }

  /**
   * Updates tasks data and reapplies search if active
   */
  async updateTasksData(newTasks: any[]) {
    this.all_tasks = [...newTasks];
    this.tasks = [...this.all_tasks];
    await this.FormatData();
    if (this.is_searching && this.current_search_query) {
      const fieldMappingConfig = this.searchService.createTaskFieldMapping();
      this.tasks = this.GetFilteredTasks(this.searchService.filterDataByQuery(this.all_tasks, this.current_search_query, fieldMappingConfig));
    } else {
      this.tasks = this.GetFilteredTasks([...this.all_tasks]);
    }
  }

  GetRelativeDateDisplay(task) {
    if (!this.showRelativeDates) return;
    if (!this.project) return;
    // Shown under a "Relative Due Date" header, so just the relative part
    return TaskDueDateLabel(task);

  }

  /**
   * Called when tasks_with_my_skills input property changes
   * Add your filtering logic here
   */
  onTasksWithMySkillsChange(value: boolean) {
    this.tasks = this.GetFilteredTasks(this.all_tasks);
  }
  onTaskViewChange(value: string) {
    this.tasks = this.GetFilteredTasks(this.all_tasks);
  }


  GetFilteredTasks(tasks: any[] | null | undefined): any[] {
    const list = tasks ?? [];
    let to_return = list.filter(c =>
      this.HideRowLogic(c) &&
      this.MatchesQuickSearch(c)
    );
    return to_return
  }

  // Quick search (mirrors components/projects/table/table.component.ts's quick search) - searches
  // task title, internal status label, system status label (via FormatTaskColumnForExport so it
  // matches exactly what's rendered, including the paused/"requires finishing previous task"
  // overrides), linked-to name, and assigned team member. Routed through GetFilteredTasks, the
  // single place every call site (search-modal results, view-toggle changes, initial load, etc.)
  // already filters through, so this applies everywhere without needing to be threaded into each
  // call site separately. Debounced 300ms via
  // quickSearchDebounceTimer so filtering doesn't recompute on every keystroke - quickSearchInput
  // tracks the raw typed value for instant display, quickSearchPending drives the "Searching..."
  // indicator while quickSearchTerm (the actually-applied value) is still catching up.
  quickSearchTerm = '';
  quickSearchInput = '';
  quickSearchPending = false;
  private quickSearchDebounceTimer: any;

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term;
    this.quickSearchPending = true;
    clearTimeout(this.quickSearchDebounceTimer);
    this.quickSearchDebounceTimer = setTimeout(() => {
      this.quickSearchTerm = term;
      this.quickSearchPending = false;
      this.tasks = this.GetFilteredTasks(this.all_tasks);
    }, 300);
  }

  private MatchesQuickSearch(task: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    const values = [
      task?.title,
      this.GetInternalStatusByGuid(task?.internal_status)?.title,
      this.FormatTaskColumnForExport(task, 'status'),
      task?.linked_name,
      task?.team_member_assigned_name,
    ];
    return values.some(value => `${value ?? ''}`.toLowerCase().includes(query));
  }

  // Worker = project_team_members.role 2 ("Worker") or 3 ("Worker (Alt)"); Follower = role 4. Checked
  // explicitly against the task's parent project's team_members list - an all_team_members project has
  // no explicit role for anyone, so it never matches Worker or Follower, only the "All" filter.
  IsCurrentUserInProjectRole(task: any, role: 'worker' | 'follower'): boolean {
    const project = task.project;
    if (!project) return false;
    const my_guid = this.globalService.GetCurrentUser()?.guid;
    if (!my_guid) return false;
    if (project.all_team_members) return false;
    const tm = (project.team_members || []).find((t: any) => t.guid === my_guid);
    if (!tm) return false;
    if (role === 'worker') return tm.role == 2 || tm.role == 3;
    return tm.role == 4;
  }

  HideRowLogic(task) {

    if (this.edit_mode) return true;

    if (this.project_person_filter !== 'all' && !this.IsCurrentUserInProjectRole(task, this.project_person_filter)) return false;

    if (this.tasks_with_my_skills) {
      let has_skils = false;
      //Loop through the prorject and see if any tasks have my skills
      if (!this.team_member || !this.team_member.skills || this.team_member.skills.length == 0) return false;

      if (!task.skills || task.skills.length == 0) return false;
      for (const skill of task.skills) {
        if (!skill || !skill.guid) continue;
        if (this.team_member.skills && this.team_member.skills.some((ms: any) => ms && ms.guid === skill.guid)) {
          has_skils = true;
          break;
        }
      }
      if (!has_skils) return false;
    }
    if (this.overdue_tasks_only) {
      if (task.status == TaskStatus.completed) return false;
      if (!task.due_date || !dayjs(task.due_date).isBefore(dayjs(), 'day')) return false;
    } else if (this.in_progress_tasks_only) {
      if (task.status == TaskStatus.completed || !(task.progress > 0)) return false;
    } else if (this.awaiting_client_tasks_only) {
      if (task.status != TaskStatus.awaiting_client) return false;
    } else if (this.active_tasks_only) {
      if (task.status != TaskStatus.active && task.status != TaskStatus.inactive) {
        return false;
      }
    }
    if (this.start_date_passed) {
      if (task.start_date_time > this.today.getTime()) {
        return false;
      }
    }

    if (this.tasks_view == "mine" || this.tasks_view == "others") {

      if (!task.assigned_member_guid) return false;
      if (task.assigned_member_guid != this.team_member.guid) return false;
    }
    else if (this.tasks_view == "unclaimed") {
      if (task.assignment_settings != 1) return false;
      if (task.assigned_member_guid) return false;
    }
    else if (this.tasks_view == "unassigned") {
      if (task.assigned_member_guid) return false;
    }

    return true;
  }

  private updateExpandedRowKeys(oldGuid: string, newGuid: string) {
    // Update expanded row keys for all tables
    if (this.tables) {
      this.tables.forEach(table => {
        if (table.expandedRowKeys && table.expandedRowKeys[oldGuid]) {
          table.expandedRowKeys[oldGuid] = false;
          table.expandedRowKeys[newGuid] = true;
        }
      });
    }
  }


  /**
   * Sync availableColumns visibility state from hideColumns array
   */
  syncColumnsFromHideColumns() {
    this.availableColumns.forEach(col => {
      col.visible = !this.hideColumns.includes(col.key);
    });
  }

  /**
   * Sync hideColumns array from availableColumns visibility state
   */
  syncHideColumnsFromColumns() {
    this.hideColumns = this.availableColumns
      .filter(col => !col.visible)
      .map(col => col.key);
  }

  // Puts the columns in the order the column picker left them (saved with the other table preferences). Columns the
  // saved order doesn't know about - added since - go after the known ones, in their default order.
  private ApplyColumnOrder(order: string[]) {
    // An order saved before Client Year End Date existed: it goes right after the client's name
    if (!order.includes('client_year_end_date')) {
      order = [...order];
      const linked = order.indexOf('linked_name');
      if (linked >= 0) order.splice(linked + 1, 0, 'client_year_end_date');
    }
    const position = (key: string) => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
    this.availableColumns = [...this.availableColumns].sort((a, b) => position(a.key) - position(b.key));
  }

  /**
   * Save filter preferences to cookies
   */
  UpdateFilterCookies() {
    if(this.ignore_cookies) return;
    const hideColumnsValue = this.availableColumns
      .filter(col => !col.visible)
      .map(col => col.key);
    const filterPreferences = {
      hideColumns: hideColumnsValue,
      columnOrder: this.availableColumns.map(col => col.key),
      max_per_page: this.max_per_page
    };
    this.cookieService.set('tasks_hideColumns', JSON.stringify(filterPreferences), 30, '/');
  }

  /**
   * Restore filter preferences from cookies
   */
  RestoreFilterCookies() {
    if(this.ignore_cookies) return;
    const hideColumnsCookie = this.cookieService.get("tasks_hideColumns");
    if (hideColumnsCookie === null || hideColumnsCookie === '') return;

    // Backwards compatible with the old raw comma-separated-string format this cookie used to hold.
    if (hideColumnsCookie.trim().startsWith('{')) {
      try {
        const filterPreferences = JSON.parse(hideColumnsCookie);
        if (filterPreferences.hideColumns !== undefined) {
          this.hideColumns = filterPreferences.hideColumns;
          this.syncColumnsFromHideColumns();
        }
        if (Array.isArray(filterPreferences.columnOrder)) this.ApplyColumnOrder(filterPreferences.columnOrder);
        if (filterPreferences.max_per_page !== undefined) {
          this.max_per_page = filterPreferences.max_per_page;
          this.restored_max_per_page = true;
        }
      } catch (e) {
        console.error('Error parsing filter cookies:', e);
      }
    } else {
      this.hideColumns = hideColumnsCookie.split(',');
      this.syncColumnsFromHideColumns();
    }
  }

  // Rows-per-page changed from the paginator's own dropdown - PrimeNG doesn't have a two-way [(rows)]
  // binding, so this is caught via (onPage) instead, same as any other filter change.
  private restored_max_per_page = false;
  OnTablePageChange(event: any) {
    if (event?.rows && event.rows !== this.max_per_page) {
      this.max_per_page = event.rows;
      this.UpdateFilterCookies();
    }
  }

  /**
   * Open the column picker modal
   */
  async OpenColumnPicker() {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));

    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        columns: columnsCopy
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        this.availableColumns = data.data.columns;
        this.syncHideColumnsFromColumns();
        this.UpdateFilterCookies();
      }
    });

    return await modal.present();
  }

  async OpenExportPdf() {
    return this.OpenExportColumnPicker('pdf', false);
  }

  async OpenExportExcel() {
    return this.OpenExportColumnPicker('excel', false);
  }

  // Only meaningful once the user has multi-select turned on and at least one row checked.
  CanExportSelected(): boolean {
    return this.bulk_select_active && this.selectedTasks.length > 0;
  }

  async OpenExportSelectedPdf() {
    return this.OpenExportColumnPicker('pdf', true);
  }

  async OpenExportSelectedExcel() {
    return this.OpenExportColumnPicker('excel', true);
  }

  /**
   * Open the column picker modal in "pick columns to export" mode, then hand the currently visible
   * (custom-filtered + PrimeNG-column-filtered) rows - or just the checked rows, if selected_only -
   * off to the matching export service.
   */
  private async OpenExportColumnPicker(format: 'pdf' | 'excel', selected_only: boolean) {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));
    const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';

    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        columns: columnsCopy,
        header: `Export ${selected_only ? 'Selected ' : ''}Tasks to ${formatLabel}`,
        selectLabel: 'Select columns to include:',
        confirmLabel: 'Export'
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      const columns: ColumnConfig[] = data?.data?.columns;
      if (!columns) return;
      const visibleColumns = columns.filter(c => c.visible);
      if (format === 'pdf') this.ExportTasksPdf(visibleColumns, selected_only);
      else this.ExportTasksExcel(visibleColumns, selected_only);
    });

    return await modal.present();
  }

  private BuildTaskExportRows(columns: ColumnConfig[], selected_only: boolean): string[][] {
    const rows = selected_only ? this.selectedTasks : this.GetCurrentlyVisibleTaskRows();
    return rows.map(task => columns.map(c => this.FormatTaskColumnForExport(task, c.key)));
  }

  private ExportTasksPdf(columns: ColumnConfig[], selected_only: boolean = false) {
    this.tablePdfExportService.Export('Tasks', columns, this.BuildTaskExportRows(columns, selected_only), `tasks-export-${dayjs().format('YYYY-MM-DD')}.pdf`);
  }

  private ExportTasksExcel(columns: ColumnConfig[], selected_only: boolean = false) {
    this.tableExcelExportService.Export('Tasks', columns, this.BuildTaskExportRows(columns, selected_only), `tasks-export-${dayjs().format('YYYY-MM-DD')}.xlsx`);
  }

  // Reads whatever's currently rendered on screen straight off the live PrimeNG Table instance -
  // filteredValue already reflects every p-columnFilter the user has applied, and .value is itself
  // the custom-toggle-filtered array (see GetFilteredTasks/HideRowLogic), so this needs no
  // filter-matching logic of its own.
  private GetCurrentlyVisibleTaskRows(): any[] {
    const t = this.tables?.first as any;
    return t ? (t.filteredValue ?? t.value ?? []) : [];
  }

  // Mirrors how each column actually renders in the table body (table.component.html), including the
  // paused/"requires finishing previous task" overrides ahead of the plain task_status label.
  private FormatTaskColumnForExport(task: any, key: string): string {
    switch (key) {
      case 'title': return task.title || '';
      case 'internal_status': return this.GetInternalStatusByGuid(task.internal_status)?.title || '';
      case 'status':
        if (this.IsTaskPaused(task)) return task.status == 21 ? String(new StatusPipe().transform(task.status)) : 'Paused';
        if (task.status == 8) return 'Requires Finishing Previous Task In Project';
        return String(new StatusPipe().transform(task.status));
      case 'linked_name': return task.linked_name || '';
      case 'client_year_end_date': return task.client_year_end_date ? dayjs(task.client_year_end_date).format('MMM D') : '';
      case 'project_title': return task.project_title || '';
      case 'team_member_assigned_name': return task.team_member_assigned_name || '';
      case 'progress': return `${task.progress ?? 0}%`;
      case 'start_date': return task.start_date ? dayjs(task.start_date).format('MMM D, YYYY') : '';
      case 'due_date': return task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : '';
      case 'completed_date': return task.completed_date ? dayjs(task.completed_date).format('MMM D, YYYY') : '';
      case 'skills': return (task.skills || []).map((s: any) => s.title).join(', ');
      case 'document_request_complete': return task.document_request_complete ? dayjs(task.document_request_complete).format('MMM D, YYYY h:mm A') : '';
      case 'last_client_document_upload': return task.last_client_document_upload ? dayjs(task.last_client_document_upload).format('MMM D, YYYY h:mm A') : '';
      default: return '';
    }
  }

  IsPreviousTaskCompleted(project, task) {
    if (!project || !project.tasks || project.tasks.length === 0) {
      return true;
    }
    task.has_checked_status = true;
    if (!project.force_order) {
      return true;
    }

    const currentTaskIndex = project.tasks.findIndex(t => t.guid === task.guid);
    if (currentTaskIndex <= 0) {
      return true;
    }

    const previousTask = project.tasks[currentTaskIndex - 1];
    if (previousTask.status == TaskStatus.completed) {
      return true;
    }

    if (previousTask.status != TaskStatus.completed) {
      return false;
    }
    else return true;
  }

  // ── Inline editing helpers ────────────────────────────────────────────────

  get team_members_with_unassigned(): any[] {
    return [{ guid: '', display_name: 'Unassigned' }, ...this.team_members];
  }

  /**
   * Returns true when the current user may reassign the given task
   * (firm admin OR the project's team leader).
   */
  IsAdminOrProjectLeader(task: any): boolean {
    if (this.globalService.GetCurrentUser().is_admin) return true;
    const proj = this.project || task.project;
    if (proj && proj.team_leader && proj.team_leader.guid === this.globalService.GetCurrentUser().guid) return true;
    return false;
  }

  GetTaskRowColour(task: any): string {
    const proj = this.project || task.project;
    return proj?.row_colour;
  }

  // True when the task itself is paused, or its whole project is paused (which cascades paused=1
  // down to every task/step server-side, but an already-loaded task/project in memory can lag
  // behind that until refetched - checking the project here closes that gap immediately).
  IsTaskPaused(task: any): boolean {
    if (task.paused) return true;
    const proj = this.project || task.project;
    return !!(proj && proj.paused);
  }

  // ── Date inline editing ───────────────────────────────────────────────────

  StartDateEdit(task: any, field: string, event: Event) {
    if (!this.IsAdminOrProjectLeader(task)) return;
    this.CancelMemberEdit();
    this.inline_edit_task = task;
    this.inline_edit_task_guid = task.guid;
    this.inline_edit_field = field;
    this.inline_edit_date = task[field] ? dayjs(task[field]).toDate() : null;

    const proj = this.project || task.project;
    const projStart: string | null = proj?.internal_start_date ?? null;
    const projEnd: string | null = proj?.internal_end_date ?? null;

    if (field === 'start_date') {
      // min: project start  |  max: earlier of task due_date and project end
      this.inline_edit_min_date = projStart ? dayjs(projStart).toDate() : null;
      const candidates = [task.due_date, projEnd].filter(Boolean);
      this.inline_edit_max_date = candidates.length
        ? dayjs(candidates.reduce((a, b) => a < b ? a : b)).toDate()
        : null;
    } else {
      // due_date  |  min: later of task start_date and project start  |  max: project end
      const minCandidates = [task.start_date, projStart].filter(Boolean);
      this.inline_edit_min_date = minCandidates.length
        ? dayjs(minCandidates.reduce((a, b) => a > b ? a : b)).toDate()
        : null;
      this.inline_edit_max_date = projEnd ? dayjs(projEnd).toDate() : null;
    }

    this.dateOverlay.show(event);
  }

  async SaveDateEdit() {
    const task = this.inline_edit_task;
    if (!task || !this.inline_edit_date) { this.CancelDateEdit(); return; }
    const field = this.inline_edit_field;
    const formatted = dayjs(this.inline_edit_date).format('YYYY-MM-DD');

    const proj = this.project || task.project;
    const projStart: string | null = proj?.internal_start_date ?? null;
    const projEnd: string | null = proj?.internal_end_date ?? null;

    // Guard: start_date must not be after due_date
    if (field === 'start_date' && task.due_date && formatted > task.due_date) {
      this.globalService.presentToastMessage('Start date cannot be after the due date.', false);
      return;
    }
    // Guard: due_date must not be before start_date
    if (field === 'due_date' && task.start_date && formatted < task.start_date) {
      this.globalService.presentToastMessage('Due date cannot be before the start date.', false);
      return;
    }
    // Guard: neither date can fall outside the project window
    if (projStart && formatted < projStart) {
      this.globalService.presentToastMessage(`Date cannot be before the project start date (${projStart}).`, false);
      return;
    }
    if (projEnd && formatted > projEnd) {
      this.globalService.presentToastMessage(`Date cannot be after the project end date (${projEnd}).`, false);
      return;
    }

    task[field] = formatted;
    this.dateOverlay.hide();
    this.inline_edit_task = null;
    this.inline_edit_task_guid = null;
    this.inline_edit_field = null;
    this.inline_edit_date = null;
    this.inline_edit_min_date = null;
    this.inline_edit_max_date = null;
    this.inline_edit_disabled_tooltip = null;
    try {
      await this.apiService.put(`tasks/${task.guid}`, task);
      this.globalService.presentToastMessage('Date updated successfully', true);
      this.taskModified.emit({ data: task });
    } catch (e) {
      this.globalService.presentToastMessage('Failed to update date', false);
    }
  }

  CancelDateEdit() {
    this.inline_edit_task = null;
    this.inline_edit_task_guid = null;
    this.inline_edit_field = null;
    this.inline_edit_date = null;
    this.inline_edit_min_date = null;
    this.inline_edit_max_date = null;
    this.inline_edit_disabled_tooltip = null;
    if (this.dateOverlay) this.dateOverlay.hide();
  }

  // ── Team-member inline editing ────────────────────────────────────────────

  StartMemberEdit(task: any) {
    this.CancelDateEdit();
    this.inline_edit_member_task_guid = task.guid;
  }

  async ClaimTask(task: any) {
    const alert = await this.alertController.create({
      header: 'Claim Task',
      message: `Claim "${task.title}"? This will assign the task to you.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Claim',
          handler: async () => {
            const previousGuid = task.assigned_member_guid;
            const currentUser = this.globalService.GetCurrentUser();
            task.assigned_member_guid = currentUser.guid;
            task.team_member_assigned_name = this.GetAssignedTeamMember(task.assigned_member_guid);
            try {
              await this.apiService.put(`tasks/${task.guid}/assign`, { team_member_guid: task.assigned_member_guid });
              this.taskModified.emit({ data: task });
            } catch (e) {
              task.assigned_member_guid = previousGuid;
              task.team_member_assigned_name = this.GetAssignedTeamMember(previousGuid);
              this.globalService.presentToastMessage('Failed to claim task', false);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async ReleaseTask(task: any) {
    const alert = await this.alertController.create({
      header: 'Release Task',
      message: `Release "${task.title}"? This will unassign the task from you.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Release',
          handler: async () => {
            const previousGuid = task.assigned_member_guid;
            task.assigned_member_guid = '';
            task.team_member_assigned_name = this.GetAssignedTeamMember('');
            try {
              await this.apiService.put(`tasks/${task.guid}/assign`, { team_member_guid: previousGuid, release: true });
              this.taskModified.emit({ data: task });
            } catch (e) {
              task.assigned_member_guid = previousGuid;
              task.team_member_assigned_name = this.GetAssignedTeamMember(previousGuid);
              this.globalService.presentToastMessage('Failed to release task', false);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async SaveMemberAssignment(task: any, memberGuid: string) {
    const previousGuid = task.assigned_member_guid;
    task.assigned_member_guid = memberGuid || '';
    task.team_member_assigned_name = this.GetAssignedTeamMember(task.assigned_member_guid);
    this.inline_edit_member_task_guid = null;
    try {
      await this.apiService.put(`tasks/${task.guid}/assign`, { team_member_guid: task.assigned_member_guid });
      this.globalService.presentToastMessage('Assignment updated', true);
      this.taskModified.emit({ data: task });
    } catch (e) {
      task.assigned_member_guid = previousGuid;
      task.team_member_assigned_name = this.GetAssignedTeamMember(previousGuid);
      this.globalService.presentToastMessage('Failed to update assignment', false);
    }
  }

  CancelMemberEdit() {
    this.inline_edit_member_task_guid = null;
  }

  // ── Mark task as complete ───────────────────────────────────────────────

  CanMarkTaskAsComplete(task: any): boolean {
    if (this.IsAdminOrProjectLeader(task)) return true;
    const currentUser = this.globalService.GetCurrentUser();
    if (task.assigned_member_guid && task.assigned_member_guid === currentUser.guid) return true;
    return false;
  }

  async MarkTaskAsComplete(task: any) {
    const alert = await this.alertController.create({
      header: 'Force Complete Task',
      message: 'Are you sure you want to force complete this task? It will automatically mark all actions as complete. This action cannot be undone.',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.ForceCompleteTask(task, true);
            await this.globalService.dismissLoading();
            await this.RefreshOneTask(task.guid);
          }
        }
      ]
    });
    await alert.present();
  }

  // Shared by MarkTaskAsComplete (single row) and BulkMarkTasksAsComplete - fetches every step on the
  // task and marks them all complete, then refreshes the task's own status plus its mirrored entry
  // inside project.tasks (used by the force-order "previous task" check elsewhere in this component).
  // show_step_loading_text mirrors the single-row flow's per-step loading message; the bulk flow shows
  // its own per-task progress instead, so it passes false to skip re-showing this loading text per step.
  private async ForceCompleteTask(task: any, show_step_loading_text: boolean) {
    let task_details: any = await this.apiService.get(`tasks/${task.guid}`);
    let steps = task_details?.data?.[0]?.steps ?? [];
    for (let step of steps) {
      if (show_step_loading_text) await this.globalService.presentLoading(`Marking Task as Complete. Action: ${step.title}`, 99999999);
      await this.apiService.put(`steps/${step.guid}/complete/true?skip_start=1`, null);
    }

    // Refresh the task as well
    await this.GetTaskDetails(task, false);
    this.tasks.forEach(t => {
      if (t.project) {
        if (t.project_guid == task.project_guid) {
          t.project.tasks.forEach(t_pro => {
            if (t_pro.guid == task.guid) {
              t_pro.status = task.status;
            }
          });
        }
      }
    });
  }

  // ── Bulk selection actions ──────────────────────────────────────────────

  ClearSelectedTasks() {
    this.selectedTasks = [];
    this.selectedTasksEvent.emit(this.selectedTasks);
  }

  // Once the maximum is ticked, the other checkboxes are disabled until one is un-ticked
  get at_max_selections(): boolean {
    return this.max_allowed_selections < 9999999 && (this.selectedTasks || []).length >= this.max_allowed_selections;
  }

  IsTaskSelected(task: any): boolean {
    return (this.selectedTasks || []).some(t => t.guid === task.guid);
  }

  RowChange($event) {
    this.selectedTasksEvent.emit(this.selectedTasks);
  }

  private BulkCompletableTasks(): any[] {
    return (this.selectedTasks || []).filter(t => t.status != TaskStatus.completed && !this.IsTaskPaused(t) && this.CanMarkTaskAsComplete(t));
  }

  async BulkMarkTasksAsComplete() {
    const eligible = this.BulkCompletableTasks();
    if (eligible.length === 0) {
      this.globalService.presentToastMessage('None of the selected tasks can be force completed.', false);
      return;
    }
    const skipped = this.selectedTasks.length - eligible.length;
    const alert = await this.alertController.create({
      header: 'Force Complete Tasks',
      message: `Are you sure you want to force complete ${eligible.length} task(s)? It will automatically mark all actions as complete. This action cannot be undone.` +
        (skipped > 0 ? ` ${skipped} selected task(s) will be skipped (already complete, paused, or you don't have permission).` : ''),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            for (let i = 0; i < eligible.length; i++) {
              await this.globalService.presentLoading(`Completing task ${i + 1} of ${eligible.length}...`, 99999999);
              await this.ForceCompleteTask(eligible[i], false);
              this.taskModified.emit({ data: eligible[i] });
            }
            await this.globalService.dismissLoading();
            await this.RefreshTasksInPlace(eligible.map(t => t.guid));
            this.globalService.presentToastMessage(`Completed ${eligible.length} task(s)`, true);
            this.ClearSelectedTasks();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkChangeTaskInternalStatus(status_guid: string) {
    if (!this.selectedTasks || this.selectedTasks.length === 0) return;
    const tasks = [...this.selectedTasks];
    const status_label = this.GetInternalStatusByGuid(status_guid)?.title || 'the selected status';
    const alert = await this.alertController.create({
      header: 'Change Internal Status',
      message: `Are you sure you want to set the internal status of ${tasks.length} task(s) to "${status_label}"? This action cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Updating internal status for ${tasks.length} task(s)...`, 99999999);
            await Promise.all(tasks.map(task => {
              task.internal_status = status_guid;
              return this.apiService.put(`tasks/${task.guid}`, { internal_status: task.internal_status });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Internal status updated for ${tasks.length} task(s)`, true);
            this.bulk_internal_status_guid = '';
            this.ClearSelectedTasks();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkAssignTeamMember(member_guid: string) {
    if (!this.selectedTasks || this.selectedTasks.length === 0 || !member_guid) return;
    // Only a firm admin or the task's project leader may reassign it (matches the backend's
    // AssignTaskMethod default assignment_settings rule) - picking an arbitrary team member from
    // this dropdown doesn't fit the narrower "assign only yourself" self-claim exception, so that
    // exception isn't extended here.
    const tasks = this.selectedTasks.filter(t => this.IsAdminOrProjectLeader(t));
    if (tasks.length === 0) {
      this.globalService.presentToastMessage('You must be a firm admin or the project leader to reassign the selected task(s).', false);
      return;
    }
    const skipped = this.selectedTasks.length - tasks.length;
    const member_label = this.GetAssignedTeamMember(member_guid) || 'the selected team member';
    const alert = await this.alertController.create({
      header: 'Assign Team Member',
      message: `Are you sure you want to assign "${member_label}" to ${tasks.length} task(s)? This will overwrite any team member currently assigned to these tasks.` +
        (skipped > 0 ? ` ${skipped} selected task(s) will be skipped (you must be a firm admin or the project leader).` : ''),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Assigning ${tasks.length} task(s)...`, 99999999);
            await Promise.all(tasks.map(task => {
              task.assigned_member_guid = member_guid;
              task.team_member_assigned_name = this.GetAssignedTeamMember(member_guid);
              this.taskModified.emit({ data: task });
              return this.apiService.put(`tasks/${task.guid}/assign`, { team_member_guid: member_guid });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Assigned ${tasks.length} task(s) to ${member_label}`, true);
            this.bulk_assigned_member_guid = '';
            this.ClearSelectedTasks();
          }
        }
      ]
    });
    await alert.present();
  }

  async BulkUnassignTeamMember() {
    if (!this.selectedTasks || this.selectedTasks.length === 0) return;
    // Same rule as BulkAssignTeamMember - only a firm admin or the task's project leader may
    // clear its assignment.
    const tasks = this.selectedTasks.filter(t => this.IsAdminOrProjectLeader(t));
    if (tasks.length === 0) {
      this.globalService.presentToastMessage('You must be a firm admin or the project leader to unassign the selected task(s).', false);
      return;
    }
    const skipped = this.selectedTasks.length - tasks.length;
    const alert = await this.alertController.create({
      header: 'Unassign Team Member',
      message: `Are you sure you want to unassign the team member from ${tasks.length} task(s)?` +
        (skipped > 0 ? ` ${skipped} selected task(s) will be skipped (you must be a firm admin or the project leader).` : ''),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: async () => {
            await this.globalService.presentLoading(`Unassigning ${tasks.length} task(s)...`, 99999999);
            await Promise.all(tasks.map(task => {
              task.assigned_member_guid = '';
              task.team_member_assigned_name = this.GetAssignedTeamMember('');
              this.taskModified.emit({ data: task });
              return this.apiService.put(`tasks/${task.guid}/assign`, { team_member_guid: '' });
            }));
            await this.globalService.dismissLoading();
            this.globalService.presentToastMessage(`Unassigned ${tasks.length} task(s)`, true);
            this.bulk_assigned_member_guid = '';
            this.ClearSelectedTasks();
          }
        }
      ]
    });
    await alert.present();
  }

  // ── Unpause task ────────────────────────────────────────────────────────

  async UnpauseTaskClick(task: any) {
    const proj = this.project || task.project;
    const pausedByProject = !task.paused && proj?.paused;

    if (pausedByProject) {
      const alert = await this.alertController.create({
        header: 'Project Paused',
        message: 'This task is paused because its whole project is on pause. Resume the project from the project details page to unpause it.',
        buttons: [{ text: 'OK', role: 'cancel' }]
      });
      await alert.present();
      return;
    }

    if (!this.IsAdminOrProjectLeader(task)) return;

    const alert = await this.alertController.create({
      header: 'Task Paused',
      message: 'This task is paused. Do you want to unpause it and resume normal activity?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Unpause',
          handler: async () => {
            await this.globalService.presentLoading('Unpausing Task', 30000);
            await this.apiService.put(`tasks/${task.guid}/pause/0`, null);
            task.paused = false;
            await this.globalService.dismissLoading();
            await this.RefreshOneTask(task.guid);
          }
        }
      ]
    });
    await alert.present();
  }

  // ── Skills inline editing ─────────────────────────────────────────────────

  async OpenSkillsModal(task: any) {
    if (!this.IsAdminOrProjectLeader(task)) return;
    if (!task.skills) task.skills = [];
    const skills_copy = JSON.stringify(task.skills);

    const modal = await this.modalController.create({
      component: SkillsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'team_members',
        selected_tags: task.skills,
        title: 'Select Skills',
        source_header: 'Available Skills',
        target_header: 'Assigned Skills',
        name: 'Skill'
      }
    });

    modal.onDidDismiss().then(async (data: any) => {
      if (data && data.data) {
        task.skills = data.data;
        try {
          await this.apiService.put(`tasks/${task.guid}`, task);
          this.globalService.presentToastMessage('Skills updated', true);
          this.taskModified.emit({ data: task });
        } catch (e) {
          task.skills = JSON.parse(skills_copy);
          this.globalService.presentToastMessage('Failed to update skills', false);
        }
      } else {
        task.skills = JSON.parse(skills_copy);
      }
    });

    return await modal.present();
  }


  async presentProjectDetails(task) {

    if (task.is_fake) {
      let fake_guid = task.guid;
      //Create the project
      this.globalService.presentLoading("Loading Task...", 99999999);
      let create_data: any = await this.apiService.post('projects', task.project);
      this.globalService.dismissLoading();

      if (create_data.success) {
        let tasks = create_data.data[0].tasks;
        let selected_task = null;
        tasks.forEach(task_loop => {
          if (task_loop.title == task.title) {
            selected_task = task_loop;
          }
        });
        const indexToRemove = this.tasks.findIndex((item) => item.guid === task.guid);
        this.tasks.splice(indexToRemove, 1);
        task = JSON.parse(JSON.stringify(selected_task));
        this.tasks.splice(indexToRemove, 0, task);
      }
      else {
        if (create_data.status_code == 208) {
          let project_guid = create_data.data;
          let project_data: any = await this.apiService.get(`projects/${project_guid}`);

          let tasks = project_data.data[0].tasks;
          let selected_task = null;
          tasks.forEach(task_loop => {
            if (task_loop.title == task.title) {
              selected_task = task_loop;
            }
          });
          selected_task.project_guid = project_data.data[0].guid;
          const indexToRemove = this.tasks.findIndex((item) => item.guid === task.guid);
          this.tasks.splice(indexToRemove, 1);
          task = JSON.parse(JSON.stringify(selected_task));
          this.tasks.splice(indexToRemove, 0, task);
        }
      }
    }

    const modal = await this.modalController.create({
      component: ProjectDetailsPage,
      cssClass: 'large-modal',
      componentProps: {
        guid: task.project_guid
      },
      backdropDismiss: false
    })
    modal.onDidDismiss()
      .then((data: any) => {
        if (data.data?.refresh) this.RefreshTasksInPlace([], [task.project_guid]);


      });
    return await modal.present()
  }


  async StartTimerForTask(task: any) {
    if (this.globalService.timer_project?.guid === task.guid) {
      this.globalService.StartTimer();
      return;
    }
    const timerActive = this.globalService.timer_wall_start || this.globalService.total_seconds > 0;
    if (timerActive) {
      const alert = await this.alertController.create({
        header: 'Timer Already Running',
        message: 'A timer is already running. Restart it for "' + task.title + '"?',
        buttons: [
          { text: 'Keep Current', role: 'cancel' },
          {
            text: 'Restart',
            handler: () => {
              this.globalService.ResetTimer();
              this.globalService.timer_project = task;
              this.globalService.timer_project_type = 'task';
              this.globalService.StartTimer();
              this.globalService.presentToastMessage('Timer started for: ' + task.title, true);
            }
          }
        ]
      });
      return await alert.present();
    }
    this.globalService.timer_project = task;
    this.globalService.timer_project_type = 'task';
    this.globalService.StartTimer();
    this.globalService.presentToastMessage('Timer started for: ' + task.title, true);
  }

  async StopAndLogTask(task: any) {
    this.globalService.StopTimer();
    const now = new Date();
    const start = this.globalService.GetTimerStart(now);
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { initial_start_time: start, initial_end_time: now, initial_worked_ms: this.globalService.total_seconds, reset_timer_on_save: true, task, project: this.project }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data?.data?.success) this.globalService.ResetTimer();
    });
    return await modal.present();
  }

  async ConfirmDiscardTimerTask(task: any) {
    const alert = await this.alertController.create({
      header: 'Discard Timer?',
      message: `Are you sure you want to discard the timer for "${task.title}"?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Discard', role: 'destructive', handler: () => this.globalService.ResetTimer() }
      ]
    });
    await alert.present();
  }

  async OpenManualTimeEntryForTask(task: any) {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { task, project: this.project }
    });
    return await modal.present();
  }

}
